/**
 * ThemeContext — Light / Dark / System appearance (QA S-02).
 *
 * The app is dark-first. The user picks one of three modes:
 *   - 'system' (default): follow the OS via prefers-color-scheme, live.
 *   - 'light' / 'dark': an explicit override.
 *
 * We resolve the mode to a concrete theme ('light' | 'dark') and stamp it on
 * <html data-theme="...">, which flips the CSS token overrides in App.css.
 * The choice is persisted in localStorage. An inline script in index.html
 * applies the same logic before React mounts to avoid a flash of the wrong
 * theme; this provider keeps it in sync afterwards.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type ThemeMode = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'firewatch-theme';

// Meta theme-color per resolved theme — drives the Android/PWA status-bar tint.
// Keep in sync with --bg-primary in App.css (matches the existing dark value).
const THEME_COLOR: Record<ResolvedTheme, string> = {
  dark: '#0d0d0f',
  light: '#f4f5f7',
};

interface ThemeContextValue {
  /** The user's chosen mode. */
  mode: ThemeMode;
  /** The concrete theme currently applied (mode 'system' resolved). */
  resolvedTheme: ResolvedTheme;
  setMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function prefersDark(): boolean {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return true; // dark-first fallback
  }
}

function readStoredMode(): ThemeMode {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    /* private mode / blocked storage — fall through */
  }
  return 'system';
}

function resolve(mode: ThemeMode): ResolvedTheme {
  if (mode === 'system') return prefersDark() ? 'dark' : 'light';
  return mode;
}

function apply(resolved: ResolvedTheme): void {
  const root = document.documentElement;
  root.dataset.theme = resolved;
  try {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', THEME_COLOR[resolved]);
  } catch {
    /* no-op */
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(readStoredMode);
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => resolve(readStoredMode()));

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* storage blocked — the choice still applies for this session */
    }
  }, []);

  // Apply on mode change.
  useEffect(() => {
    const r = resolve(mode);
    setResolvedTheme(r);
    apply(r);
  }, [mode]);

  // When following the system, react to OS theme changes live.
  useEffect(() => {
    if (mode !== 'system') return;
    let mql: MediaQueryList;
    try {
      mql = window.matchMedia('(prefers-color-scheme: dark)');
    } catch {
      return;
    }
    const onChange = () => {
      const r = prefersDark() ? 'dark' : 'light';
      setResolvedTheme(r);
      apply(r);
    };
    // Safari <14 uses addListener/removeListener.
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, [mode]);

  const value = useMemo(
    () => ({ mode, resolvedTheme, setMode }),
    [mode, resolvedTheme, setMode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
